import { CalendarClock, Download, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { type DashboardTrack, useAdminUpdateTrack, useMe, useRefreshSteerco } from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  getSteerco,
  PATH_TO_VALUE_OPTIONS,
  pathToValueLabel,
  type Steerco,
  STEERCO_STATE_OPTIONS,
  type SteercoChip,
  steercoChipClass,
  steercoDotClass,
  type SteercoState,
  steercoStateLabel,
} from "@/lib/steerco";
import { cn } from "@/lib/utils";

import { formatLong } from "./dates";
import { InlineSelect, InlineText, InlineTextarea, splitByKind, trackScalarVars } from "./inline";

interface SteercoTabProps {
  tracks: DashboardTrack[];
  /** Program-wide target date (dashboard.deadline), used by the banner. */
  deadline: string;
  editMode: boolean;
}

const HEADERS = [
  "Function",
  "AI champion",
  "EMB workshop",
  "Use-case definition",
  "Path to value",
  "Comment",
];

const LEGEND: { state: SteercoState; label: string }[] = [
  { state: "done", label: "Done" },
  { state: "in_progress", label: "In progress" },
  { state: "at_risk", label: "At risk" },
  { state: "not_started", label: "Not started" },
];

export function SteercoTab({ tracks, deadline, editMode }: SteercoTabProps) {
  const { programTracks, championTracks } = splitByKind(tracks);
  const programTrack = programTracks[0];
  const updateTrack = useAdminUpdateTrack();
  const refreshSteerco = useRefreshSteerco();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const [downloading, setDownloading] = useState(false);

  /** Backfill the Steerco snapshot from tracked data, then report what filled. */
  const refreshFromData = () => {
    logger.debug("[dashboard]", "steerco refresh requested");
    refreshSteerco.mutate(undefined, {
      onSuccess: (result) => {
        const changes = result?.changes ?? {};
        const changeCount = Object.values(changes).reduce((sum, diffs) => sum + diffs.length, 0);
        const trackCount = result?.tracks_updated ?? 0;
        logger.debug("[dashboard]", "steerco refresh complete", { trackCount, changeCount });
        if (changeCount === 0) {
          toast.success("Steerco's already in sync with the tabs — nothing to update.");
          return;
        }
        const changeLabel = changeCount === 1 ? "update" : "updates";
        const trackLabel = trackCount === 1 ? "track" : "tracks";
        toast.success(
          `Synced ${changeCount} ${changeLabel} across ${trackCount} ${trackLabel} — glance over them on the Steerco tab.`,
        );
      },
      onError: (error) => {
        logger.error("[dashboard]", "steerco refresh failed", error);
        toast.error("Couldn't refresh from data. Please try again.");
      },
    });
  };

  /** Fetch the exec slide from the backend and trigger a browser download. */
  const downloadSlide = async () => {
    setDownloading(true);
    logger.debug("[dashboard]", "steerco slide download requested");
    try {
      const res = await fetch("/api/steerco/slide.pptx", { credentials: "include" });
      if (!res.ok) {
        throw new Error(`slide download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "project-espresso-steerco.pptx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      logger.debug("[dashboard]", "steerco slide download complete");
    } catch (error) {
      logger.error("[dashboard]", "steerco slide download failed", error);
      toast.error("Couldn't download the slide. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  /** Persist a partial Steerco patch on top of the track's full scalar set. */
  const patchSteerco = (track: DashboardTrack, change: Partial<Steerco>) => {
    const next: Steerco = { ...getSteerco(track), ...change };
    logger.debug("[dashboard]", "steerco edit", track.id, change);
    updateTrack.mutate({ ...trackScalarVars(track), steerco: next });
  };

  /** Patch a single status chip (label or state) on a champion track. */
  const patchChip = (
    track: DashboardTrack,
    key: "champion" | "ideation" | "use_case_def",
    change: Partial<SteercoChip>,
  ) => {
    const current = getSteerco(track);
    patchSteerco(track, { [key]: { ...current[key], ...change } });
  };

  // KPIs computed live from the champion tracks.
  const total = championTracks.length;
  const readyCount = championTracks.filter(
    (t) => getSteerco(t).use_case_def.state === "done",
  ).length;
  const readyPct = total > 0 ? Math.round((readyCount / total) * 100) : 0;

  // Path-to-value distribution: champion count per canonical option.
  const pathCounts = PATH_TO_VALUE_OPTIONS.map((opt) => ({
    ...opt,
    count: championTracks.filter((t) => getSteerco(t).path_to_value === opt.value).length,
  }));

  const headline = programTrack ? getSteerco(programTrack).headline : "";

  const renderChipCell = (track: DashboardTrack, key: "champion" | "ideation" | "use_case_def") => {
    const chip = getSteerco(track)[key];
    if (editMode) {
      return (
        <div className="space-y-1">
          <InlineText
            value={chip.label}
            placeholder="Label"
            onSave={(v) => patchChip(track, key, { label: v.trim() })}
          />
          <InlineSelect
            value={chip.state}
            options={STEERCO_STATE_OPTIONS}
            onSave={(v) => patchChip(track, key, { state: v as SteercoState })}
          />
        </div>
      );
    }
    return (
      <span className={steercoChipClass(chip.state)}>
        {chip.label || steercoStateLabel(chip.state)}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header: action-title + Download slide */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-primary text-[10px] font-medium uppercase">Exec snapshot</p>
          {editMode && programTrack ? (
            <InlineText
              value={headline}
              placeholder="Snapshot action-title"
              className="mt-1 h-9 text-base"
              onSave={(v) => patchSteerco(programTrack, { headline: v.trim() })}
            />
          ) : (
            <h2 className="text-foreground text-xl">
              {headline || "AI Champions — function readiness snapshot"}
            </h2>
          )}
        </div>
        {isAdmin && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshFromData}
              disabled={refreshSteerco.isPending}
              className="gap-1.5"
            >
              <RefreshCw className={cn("h-4 w-4", refreshSteerco.isPending && "animate-spin")} />{" "}
              {refreshSteerco.isPending ? "Refreshing…" : "Refresh from data"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadSlide}
              disabled={downloading}
              className="gap-1.5"
            >
              <Download className="h-4 w-4" /> {downloading ? "Preparing…" : "Download slide"}
            </Button>
          </div>
        )}
      </div>

      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="border-border bg-card rounded-md border p-4">
          <p className="text-muted-foreground text-[10px] uppercase">
            Functional readiness for PoV
          </p>
          <p className="text-foreground mt-1 text-2xl tabular-nums">
            {readyCount}
            <span className="text-muted-foreground text-base"> / {total}</span>
          </p>
          <p className="text-muted-foreground text-xs">{readyPct}% functions PoV-ready</p>
        </div>
        <div className="border-border bg-card rounded-md border p-4 sm:col-span-2">
          <p className="text-muted-foreground text-[10px] uppercase">Path to value</p>
          <ul className="mt-2 space-y-1">
            {pathCounts.map(({ value, label, count }) => (
              <li key={value} className="text-foreground flex items-center justify-between text-sm">
                <span>{label}</span>
                <span className="text-foreground tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Color legend */}
      <div className="flex flex-wrap items-center gap-4">
        {LEGEND.map(({ state, label }) => (
          <span key={state} className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <span className={`h-2.5 w-2.5 rounded-full ${steercoDotClass(state)}`} aria-hidden />
            {label}
          </span>
        ))}
      </div>

      {/* Matrix */}
      <div className="border-border overflow-x-auto rounded-md border">
        <table className="w-full min-w-[880px] border-collapse">
          <thead>
            <tr className="bg-boi-dark">
              {HEADERS.map((h) => (
                <th key={h} className="p-3 text-left text-[10px] font-medium text-white uppercase">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {championTracks.map((track) => {
              const s = getSteerco(track);
              return (
                <tr key={track.id} className="border-border border-b last:border-b-0">
                  <td className="p-3 align-top">
                    <p className="text-foreground text-sm">{track.business_unit ?? track.name}</p>
                  </td>
                  <td className="p-3 align-top">{renderChipCell(track, "champion")}</td>
                  <td className="p-3 align-top">{renderChipCell(track, "ideation")}</td>
                  <td className="p-3 align-top">{renderChipCell(track, "use_case_def")}</td>
                  <td className="p-3 align-top">
                    {editMode ? (
                      <InlineSelect
                        value={s.path_to_value}
                        options={[{ value: "", label: "—" }, ...PATH_TO_VALUE_OPTIONS]}
                        onSave={(v) => patchSteerco(track, { path_to_value: v })}
                      />
                    ) : (
                      <span className="text-foreground text-sm">
                        {pathToValueLabel(s.path_to_value)}
                      </span>
                    )}
                  </td>
                  <td className="p-3 align-top">
                    {editMode ? (
                      <InlineTextarea
                        value={s.comment}
                        rows={2}
                        placeholder="Comment"
                        onSave={(v) => patchSteerco(track, { comment: v.trim() })}
                      />
                    ) : s.comment ? (
                      <p className="text-muted-foreground text-xs leading-relaxed">{s.comment}</p>
                    ) : (
                      <span className="text-muted-foreground/70 text-xs italic">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Deadline banner */}
      <div className="border-primary bg-primary/5 flex items-center gap-3 rounded-md border p-4">
        <CalendarClock className="text-primary h-5 w-5 shrink-0" aria-hidden />
        <p className="text-foreground text-sm">
          By August 2026 ({formatLong(deadline)}), all functions must have a prioritized list of use
          cases and a defined PoV.
        </p>
      </div>
    </div>
  );
}
